import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBusy } from "../context/BusyContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import axios from "axios";
import PolicyModal from "../components/PolicyModal";
import "./css/authstyles.css";
import {
  fetchActivePolicies,
  findPolicyByTitle,
  PolicyItem,
} from "../utils/policyHelpers";

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

const SignUp = () => {
  const navigate = useNavigate();
  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();

  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    userImage: null as File | null,
  });

  const [previewImage, setPreviewImage] = useState("/images/user-icon.png");
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyItem | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPolicies = async () => {
      try {
        const data = await runBusy(() => fetchActivePolicies(BASE_URL));

        if (isMounted) {
          setPolicies(data);
        }
      } catch (error) {
        console.error("Failed to load policies:", error);
      }
    };

    loadPolicies();

    return () => {
      isMounted = false;
    };
  }, [runBusy]);

  const openPrivacyPolicy = () => {
    const policy = findPolicyByTitle(policies, "Privacy Policy");
    if (policy) setSelectedPolicy(policy);
  };

  const openTermsPolicy = () => {
    const policy =
      findPolicyByTitle(policies, "Terms of Service") ||
      findPolicyByTitle(policies, "Terms of Services");

    if (policy) setSelectedPolicy(policy);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];

    if (file) {
      setFormData({ ...formData, userImage: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Password and Confirm Password do not match.",
        confirmText: "OK",
      });
      return;
    }

    const formDataObj = new FormData();

    Object.keys(formData).forEach((key) => {
      const value = (formData as any)[key];

      if (value !== null && value !== undefined) {
        formDataObj.append(key, value);
      }
    });

    try {
      const response = await axios.post(`${BASE_URL}/signup`, formDataObj, {
        withCredentials: true,
      });

      if (response.data?.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message: response.data?.message || "Unable to complete signup.",
          confirmText: "OK",
        });
        return;
      }

      showModal({
        type: "success",
        title: "Success",
        message:
          response.data?.message ||
          "Signup successful, please check you email and click on Activate Account.",
        confirmText: "OK",
        onConfirm: () => {
          navigate("/signin", {
            state: { activationPending: true },
          });
        },
      });
    } catch (error: any) {
      console.error("Signup error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        confirmText: "OK",
      });
    }
  };

  return (
    <div className="auth-container">
      <div>
        <img
          style={{
            width: 45,
            position: "absolute",
            marginLeft: -140,
          }}
          src="/images/Lyra-Zone-Logo-New-White.png"
          alt="Lyra Zone Logo"
        />

        <label htmlFor="file-upload" className="attachment-icon">
          <img
            style={{
              width: 35,
              height: 50,
              position: "absolute",
              right: 0,
              marginRight: "-150px",
              cursor: "pointer",
              borderRadius: "50%",
              objectFit: "cover",
            }}
            src={previewImage}
            alt="Upload"
          />
        </label>
      </div>

      <form
        onSubmit={handleSubmit}
        className="auth-bg-white p-6 rounded shadow-md w-96"
      >
        <h2 className="text-2xl font-bold mb-4">Sign Up</h2>

        <input
          type="text"
          name="name"
          placeholder="Full Name"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="text"
          name="companyName"
          placeholder="Company Name (Optional)"
          onChange={handleChange}
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="email"
          name="email"
          placeholder="Email"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="tel"
          name="phoneNumber"
          placeholder="Phone Number"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="password"
          name="password"
          placeholder="Password"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <div>
          <input
            type="file"
            id="file-upload"
            name="userImage"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>

        <button type="submit" className="sign-in-btn">
          Sign Up
        </button>
      </form>

      <div className="general-text">
        <a style={{ marginLeft: 70 }} href="./signin">
          Already have account !
        </a>
      </div>

      <div className="auth-general-terms">
        By signing up, you agree to our{" "}
        <span className="auth-policy-link" onClick={openPrivacyPolicy}>
          Privacy Policy
        </span>{" "}
        and{" "}
        <span className="auth-policy-link" onClick={openTermsPolicy}>
          Terms of Service
        </span>
        .
      </div>

      <PolicyModal
        isOpen={!!selectedPolicy}
        title={selectedPolicy?.title || ""}
        body={selectedPolicy?.body || ""}
        onClose={() => setSelectedPolicy(null)}
      />
    </div>
  );
};

export default SignUp;
